import { faExpand, faRotate } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useGetPlatformDashboardQuery } from '../../../redux/services/coreApi'
import NotFoundScreen from '../../App/screens/Not Found/NotFoundScreen'
import ActivityFeed from './ActivityFeed'
import HeroStat from './HeroStat'
import Leaderboard, { assetRows, countryRows } from './Leaderboard'
import TradeTypeDonut from './TradeTypeDonut'
import VeliMilestone from './VeliMilestone'
import VolumeChart from './VolumeChart'

// How often the wall board re-pulls the snapshot. Frequent enough to feel live on a TV, gentle enough
// not to hammer the API all day.
const POLL_INTERVAL_MS = 15_000

// Accent gradients cycled across the hero tiles (by index) so the headline row reads as a spectrum
// rather than a monochrome block. Used for both the gradient number text and its corner glow.
const HERO_ACCENTS = [
  'bg-gradient-to-br from-amber-300 to-orange-500',
  'bg-gradient-to-br from-emerald-300 to-teal-500',
  'bg-gradient-to-br from-sky-300 to-blue-500',
  'bg-gradient-to-br from-violet-300 to-fuchsia-500',
  'bg-gradient-to-br from-rose-300 to-pink-500',
  'bg-gradient-to-br from-lime-300 to-green-500',
]

const Card: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = '',
  children,
}) => (
  <div
    className={`flex min-h-0 flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-4 backdrop-blur-sm xl:p-5 ${className}`}
  >
    {children}
  </div>
)

const PlatformDashboardScreen: React.FC = () => {
  const location = useLocation()

  const params = new URLSearchParams(location.search)
  const secret = params.get('secret') ?? ''
  // Optional display currency on the link (?currency=DKK|EUR); left undefined the API defaults it.
  const currency = params.get('currency') ?? undefined

  const { data, isLoading, isError, isFetching, fulfilledTimeStamp } =
    useGetPlatformDashboardQuery(
      { secret, currency },
      { skip: !secret, pollingInterval: POLL_INTERVAL_MS }
    )

  // A ticking wall clock for the header — every good ops/TV board has one.
  const [now, setNow] = useState(() => moment())
  useEffect(() => {
    const id = setInterval(() => setNow(moment()), 1000)
    return () => clearInterval(id)
  }, [])

  const enterFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.()
    } else {
      document.exitFullscreen?.()
    }
  }

  // No key in the link, or a key the server rejected: render the ordinary 404 — identical to any
  // unknown route. There's deliberately no access-key form or "wrong key" message: without a valid
  // secret the page must look like nothing worth probing, so the board only exists for someone who
  // already holds the link. The secret arrives via the `?secret=` param, never typed in here.
  if (!secret || isError) {
    return <NotFoundScreen />
  }

  // --- Initial load ------------------------------------------------------------------------------
  // Neutral, label-free spinner: a wrong key briefly lands here before the request fails and falls to
  // the 404 above, so this screen must not announce what's loading.
  if (isLoading || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0F13]">
        <FontAwesomeIcon
          icon={faRotate}
          className="animate-rotate-slow text-2xl text-white/30"
        />
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gradient-to-b from-[#0B0F13] via-[#0B0F13] to-[#0E1722] px-5 py-4 text-white xl:px-8 xl:py-5">
      {/* Header */}
      <header className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight xl:text-3xl">
            Penning <span className="text-amber-400">Live</span>
          </h1>
          <span className="flex items-center gap-1.5 rounded-full bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-300">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            Live
          </span>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <p className="text-xl font-semibold tabular-nums xl:text-2xl">
              {now.format('HH:mm:ss')}
            </p>
            <p className="text-xs text-white/40">
              Updated{' '}
              {fulfilledTimeStamp
                ? moment(fulfilledTimeStamp).format('HH:mm:ss')
                : '—'}
              {isFetching ? ' · refreshing…' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={enterFullscreen}
            aria-label="Toggle fullscreen"
            className="rounded-lg border border-white/10 p-2.5 text-white/60 transition-colors hover:bg-white/5 hover:text-white"
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        </div>
      </header>

      {/* Hero stats */}
      <section className="mb-4 grid shrink-0 grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6 xl:gap-4">
        {data.headline.map((metric, idx) => (
          <HeroStat
            key={metric.key}
            metric={metric}
            accent={HERO_ACCENTS[idx % HERO_ACCENTS.length]}
            currency={data.currency}
          />
        ))}
      </section>

      {/* Charts row */}
      <section className="mb-4 grid shrink-0 gap-4 xl:grid-cols-12">
        <Card className="xl:col-span-8">
          <h3 className="mb-2 shrink-0 text-xs font-semibold uppercase tracking-widest text-white/40">
            Trade volume · last 30 days
          </h3>
          <div className="h-48 xl:h-60">
            <VolumeChart data={data.volumePerDay} currency={data.currency} />
          </div>
        </Card>
        <div className="grid gap-4 xl:col-span-4 xl:grid-rows-2">
          <Card>
            <h3 className="mb-2 shrink-0 text-xs font-semibold uppercase tracking-widest text-white/40">
              Buy vs sell
            </h3>
            <div className="min-h-0 flex-1">
              <TradeTypeDonut
                data={data.tradeTypeSplit}
                currency={data.currency}
              />
            </div>
          </Card>
          <Card>
            <VeliMilestone progress={data.veliMigration} />
          </Card>
        </div>
      </section>

      {/* Leaderboards + live feed */}
      <section className="grid min-h-0 flex-1 gap-4 xl:grid-cols-12">
        <Card className="overflow-hidden xl:col-span-4">
          <Leaderboard
            title="Top assets · 30 days"
            rows={assetRows(data.topAssets, data.currency)}
            accentClass="bg-amber-400"
            emptyLabel="No trades yet in this window."
          />
        </Card>
        <Card className="overflow-hidden xl:col-span-4">
          <Leaderboard
            title="Customers by country"
            rows={countryRows(data.topCountries)}
            accentClass="bg-sky-400"
            emptyLabel="No customers yet."
          />
        </Card>
        <Card className="overflow-hidden xl:col-span-4">
          <ActivityFeed items={data.recentActivity} currency={data.currency} />
        </Card>
      </section>
    </div>
  )
}

export default PlatformDashboardScreen
